<template>
  <v-card class="incident-popup">
    <v-card-text v-if="!incident" class="d-flex justify-center align-center">
      <v-progress-circular indeterminate color="primary" />
    </v-card-text>
    <template v-else>
      <v-container class="ma-0 pa-2">
        <v-row>
          <v-col class="d-flex flex-grow-0 py-4">
            <v-row class="d-flex flex-row align-self-center flex-nowrap">
              <v-col cols="auto" class="mr-n5 mt-2 d-flex align-center">
                <v-avatar :color="incident.sfdiLevelColor" size="large">
                  <v-icon :size="50" class="pa-1">
                    {{ incident.category?.icon }}
                  </v-icon>
                </v-avatar>
              </v-col>

              <v-col class="d-flex flex-column align-self-center ml-2">
                <p class="text-h6 text-truncate">{{ incident.name }}</p>
                <p class="text-subtitle-2 font-weight-light text-no-wrap">
                  {{ discoveredDateTime }}
                </p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card v-if="size" variant="outlined" class="d-flex flex-column align-center" elevation="0">
              <v-icon color="red" class="mt-2" :size="20">mdi-map</v-icon>
              <div class="mt-2">{{ Number(size?.value).toFixed(2) }}</div>
              <div>{{ capitalize($t('domain.fields.fireSize')) }} ({{ size?.measurementType.unit }})</div>
            </v-card>
          </v-col>
          <v-col v-if="containment">
            <v-card variant="outlined" class="d-flex flex-column align-center" elevation="0">
              <v-progress-circular class="mt-2" :size="20" :width="3" color="blue" :model-value="containment?.value"></v-progress-circular>
              <div class="mt-2">{{ containment?.value }} {{ containment?.measurementType.unit }}</div>
              <div>{{ capitalize($t('domain.fields.containment')) }}</div>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-sheet width="100%" class="pa-6">
            <v-row v-for="d of details" :key="d.title" class="d-flex justify-space-between">
              <v-col class="pa-1">
                {{ d.title }}
              </v-col>
              <v-col class="text-end pa-1 font-weight-bold">
                {{ d.value || `-` }}
              </v-col>
            </v-row>
          </v-sheet>
        </v-row>
        <v-row>
          <v-col class="d-flex justify-start">
            <v-btn flat icon="mdi-map-marker" @click="goToMap()"></v-btn>
            <v-btn flat icon="mdi-menu-open" @click="goToIncident()"></v-btn>
          </v-col>
        </v-row>
        <FooterPopups :coordinates="props.coordinates" />
      </v-container>
    </template>
  </v-card>
</template>
<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed, ref, watch, capitalize } from 'vue'

import { useI18n } from 'vue-i18n'

import useView from '@tsyl/karonte/lib/composables/useView'
import type { Coordinates } from '@tsyl/karonte/lib/types/coordinates'
import type { Feature } from '@tsyl/karonte/lib/types/events'

import FooterPopups from '@/components/Popups/FooterPopups.vue'
import { useAdminDivisions } from '@/composables/useAdminDivisions'
import router from '@/router'
import { getIncident } from '@/services/api/incidents'
import SettingsStore from '@/stores/settingsStore.ts'
import type { Incident } from '@/types/Incident.ts'
import { getCalculationStatusDisplay } from '@/utils/calculationStatus.ts'

// const CONTAINMENT_ID = "8f8ebb51-0d8d-40c3-b9ad-865407e46ff3"

const props = defineProps({
  feature: {
    type: Object as PropType<Feature>,
    required: true
  },
  coordinates: {
    type: Object as PropType<Coordinates>,
    required: true
  }
})

const { getReactiveView } = useView()
const view = getReactiveView()

const { t } = useI18n()

const settings = SettingsStore()

const incident = ref<Incident>()

const { level1: adminDivisionLevel1, level2: adminDivisionLevel2 } = useAdminDivisions(() => ({
  latitude: incident.value?.latitude,
  longitude: incident.value?.longitude
}))

const refreshIncident = async (objectId: string) => {
  const res = await getIncident(objectId)
  if (typeof res === 'number') {
    console.error(`Incident fetch failed with status: ${res}`)
  } else {
    incident.value = res
  }
}

watch(() => props.feature?.attributes.id as string, refreshIncident, {
  immediate: true
})

const discoveredDateTime = computed(() => {
  return settings.formatStringDateTime(incident.value?.declared_timestamp || '')
})

function goToIncident() {
  router.push({
    path: '/',
    query: {
      incident: props.feature.attributes.id as string
    }
  })
}
function goToMap() {
  const coordinates: Coordinates = {
    latitude: incident.value?.latitude,
    longitude: incident.value?.longitude
  }
  view.value?.goTo(coordinates, 17)
}

const details = computed(() => {
  const array = []
  array.push({
    title: capitalize(t('domain.fields.category')),
    value: incident.value?.category?.displayName
  })
  array.push({
    title: capitalize(t('domain.fields.status')),
    value: incident.value?.status ? getCalculationStatusDisplay(incident.value.status, t).label : undefined
  })
  array.push({
    title: capitalize(t('domain.fields.complexity')),
    value: incident.value?.complexity.name
  })
  array.push({
    title: capitalize(t('domain.fields.adminDivisionLevel1')),
    value: adminDivisionLevel1.value
  })
  array.push({
    title: capitalize(t('domain.fields.adminDivisionLevel2')),
    value: adminDivisionLevel2.value
  })
  array.push({
    title: capitalize(t('domain.fields.incidentNumber')),
    value: incident.value?.incident_number
  })
  return array
})

const size = computed(() => {
  return incident.value?.measurements?.find((m) => m.measurementType.name === 'Size')
})
const containment = computed(() => {
  return incident.value?.measurements?.find((m) => m.measurementType.name === 'Containment')
})

const color = computed(() => {
  return incident.value?.sfdiLevelColor || 'red'
})
</script>

<style lang="scss" scoped>
.data-value {
  text-align: right;
}

.incident-popup {
  min-height: 300px;
  width: 400px;
}

.v-card--variant-outlined {
  border: thin solid #cfcfcf;
}

.incident-icon {
  background-color: v-bind(color);
  width: 50px;
  height: 50px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
}
</style>
